import type { InternalAxiosRequestConfig } from 'axios';
import { sessionStorageUtil } from '@utils/session-storage.util';
import { SESSION_KEYS } from '@utils/session-keys.constants';
import {
  LEGACY_WEB_SESSION_QUERY_PARAM,
  WEB_SESSION_QUERY_PARAM,
} from '@utils/web-session-query.constants';

const AUTH_TOKEN_HEADER = 'Authorization';
const WEB_SESSION_EXEMPT_PATH_SEGMENTS = ['/tridionfeed/', '/form/timaticquery', '/pnr/validate/'];

const getWebSessionIdFromUrl = (): string | null => {
  const storedWebSessionId = sessionStorageUtil.get<string>(SESSION_KEYS.WEB_SESSION_ID);
  if (typeof window === 'undefined') {
    const stored = storedWebSessionId?.trim() ?? '';
    return stored.length > 0 ? stored : null;
  }
  const params = new URLSearchParams(window.location.search);
  const fromUrl = params.get(WEB_SESSION_QUERY_PARAM) ?? params.get(LEGACY_WEB_SESSION_QUERY_PARAM);
  // Prefer sessionStorage — updated synchronously on validate; URL can lag during route transitions.
  const raw = (storedWebSessionId ?? fromUrl)?.trim() ?? '';
  return raw.length > 0 ? raw : null;
};

const isWebSessionIdExemptRequest = (config: InternalAxiosRequestConfig): boolean => {
  const urlLower = (config.url ?? '').toLowerCase();
  if (
    WEB_SESSION_EXEMPT_PATH_SEGMENTS.some(
      (segment) => urlLower.includes(segment) || config.baseURL === segment,
    )
  )
    return true;
  return false;
};

const normalizeParams = (raw: unknown): Record<string, unknown> => {
  if (raw instanceof URLSearchParams) return Object.fromEntries(raw);
  if (typeof raw === 'string') return Object.fromEntries(new URLSearchParams(raw));
  return (raw as Record<string, unknown>) ?? {};
};

const appendWebSessionIdQueryParam = (
  config: InternalAxiosRequestConfig,
  webSessionId: string,
): void => {
  const existing = normalizeParams(config.params as unknown);
  if (!existing[LEGACY_WEB_SESSION_QUERY_PARAM]) {
    config.params = { ...existing, [LEGACY_WEB_SESSION_QUERY_PARAM]: webSessionId };
  }
};

export const requestInterceptor = (
  config: InternalAxiosRequestConfig,
): InternalAxiosRequestConfig => {
  const token = sessionStorageUtil.get<string>(SESSION_KEYS.AUTH_TOKEN);

  if (token) {
    config.headers[AUTH_TOKEN_HEADER] = `Bearer ${token}`;
  }

  if (!isWebSessionIdExemptRequest(config)) {
    // to Append web session id in all API query params
    const webSessionId = getWebSessionIdFromUrl();
    if (webSessionId) appendWebSessionIdQueryParam(config, webSessionId);
  }

  return config;
};

export const requestErrorInterceptor = (error: unknown): Promise<never> => {
  return Promise.reject(error);
};
